import { useEffect, useState } from "react";
import axios from "axios";
import { useAuth, API } from "@/App";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
    Phone,
    Heart,
    Inbox,
    Calendar,
    Clock,
    Send,
    Users,
    BookOpen,
    Globe,
    Sparkles,
    Activity,
    AlertTriangle,
    TrendingUp,
    BarChart3,
    UserCheck,
    UserMinus,
    Mail,
    MessageSquare,
    CreditCard,
    LogIn,
    Ticket,
} from "lucide-react";
import { toast } from "sonner";

const SOURCE_META = {
    platform_content: { label: "KB", icon: BookOpen, color: "bg-emerald-500/15 text-emerald-300" },
    ai_generated: { label: "AI", icon: Sparkles, color: "bg-violet-500/15 text-violet-300" },
    web_search: { label: "Web", icon: Globe, color: "bg-sky-500/15 text-sky-300" },
    subscription: { label: "Sub", icon: Send, color: "bg-yellow-500/15 text-yellow-200" },
    standings: { label: "Stand.", icon: BarChart3, color: "bg-cyan-500/15 text-cyan-200" },
    prediction: { label: "Pred.", icon: TrendingUp, color: "bg-pink-500/15 text-pink-200" },
    error: { label: "Err", icon: AlertTriangle, color: "bg-red-500/15 text-red-300" },
};

const SUB_STATUS_META = {
    active: { icon: UserCheck, color: "text-electric", label: "Active" },
    pending: { icon: Clock, color: "text-yellow-400", label: "Pending" },
    opted_out: { icon: UserMinus, color: "text-white/40", label: "Opted out" },
};

const fmt = (iso) => {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return d.toLocaleString("en-GB", {
            timeZone: "Africa/Dar_es_Salaam",
            day: "2-digit", month: "short", year: "2-digit",
            hour: "2-digit", minute: "2-digit",
        });
    } catch { return "—"; }
};

const timeAgo = (iso) => {
    if (!iso) return "—";
    try {
        const t = new Date(iso).getTime();
        const diff = Date.now() - t;
        const mins = Math.floor(diff / 60000);
        if (mins < 1) return "just now";
        if (mins < 60) return `${mins}m ago`;
        const hrs = Math.floor(mins / 60);
        if (hrs < 24) return `${hrs}h ago`;
        const days = Math.floor(hrs / 24);
        if (days < 30) return `${days}d ago`;
        const months = Math.floor(days / 30);
        return `${months}mo ago`;
    } catch { return "—"; }
};

const SourcePill = ({ source }) => {
    const meta = SOURCE_META[source] || { label: source || "?", icon: Activity, color: "bg-white/10 text-white/70" };
    const Icon = meta.icon;
    return (
        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wider ${meta.color}`}>
            <Icon className="h-2.5 w-2.5" /> {meta.label}
        </span>
    );
};

const Stat = ({ label, value, sub }) => (
    <div className="bg-obsidian border border-border rounded-sm p-3">
        <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 font-heading">{label}</div>
        <div className="mt-1 text-xl font-heading font-black text-white">{value}</div>
        {sub && <div className="text-[10px] text-white/40 mt-0.5">{sub}</div>}
    </div>
);


const ChannelBadge = ({ channel, testId }) => {
    if (!channel) {
        return (
            <Badge variant="outline" className="border-white/20 text-white/40 text-[10px]" data-testid={testId}>
                unknown
            </Badge>
        );
    }
    const isSms = channel === "sms";
    return (
        <Badge
            className={
                isSms
                    ? "bg-yellow-500/15 text-yellow-300 border-yellow-500/40 border text-[10px]"
                    : "bg-electric/15 text-electric border-electric/40 border text-[10px]"
            }
            data-testid={testId}
        >
            {isSms ? (
                <><MessageSquare className="h-3 w-3 mr-1" /> SMS</>
            ) : (
                <><Globe className="h-3 w-3 mr-1" /> WEB</>
            )}
        </Badge>
    );
};


const IdentityRow = ({ icon: Icon, label, value, extra, testId }) => (
    <div className="flex items-start gap-3 py-2 border-b border-border last:border-0" data-testid={testId}>
        <Icon className="h-3.5 w-3.5 text-white/40 mt-1 flex-shrink-0" />
        <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 font-heading">{label}</div>
            <div className="text-white text-sm mt-0.5 flex items-center gap-2 flex-wrap">
                {value}
                {extra}
            </div>
        </div>
    </div>
);

export const CustomerProfileSheet = ({ phone, open, onOpenChange }) => {
    const { token } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    // "For You" preview / send state
    const [pulse, setPulse] = useState(null);
    const [pulseLoading, setPulseLoading] = useState(false);
    const [pulseSending, setPulseSending] = useState(false);

    const loadPulse = async () => {
        if (!phone) return;
        setPulseLoading(true);
        try {
            const { data } = await axios.get(
                `${API}/admin/sms/for-you/preview/${encodeURIComponent(phone)}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setPulse(data);
        } catch {
            toast.error("Preview failed");
        } finally { setPulseLoading(false); }
    };

    const sendPulse = async () => {
        if (!phone) return;
        setPulseSending(true);
        try {
            const { data } = await axios.post(
                `${API}/admin/sms/for-you/send/${encodeURIComponent(phone)}?force=true`,
                null,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (data?.status === "sent") toast.success("Digest sent");
            else if (data?.status === "skipped_no_content") toast.warning("No relevant content for this customer yet");
            else toast.info(`Status: ${data?.status || "unknown"}`);
        } catch {
            toast.error("Send failed");
        } finally { setPulseSending(false); }
    };

    useEffect(() => {
        if (!open || !phone) return;
        // Reset pulse view between phones
        setPulse(null);
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const { data } = await axios.get(
                    `${API}/admin/sms/customer/${encodeURIComponent(phone)}`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                if (!cancelled) setData(data);
            } catch (e) {
                toast.error("Failed to load customer profile");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [open, phone, token]);

    const teams = (data?.subscriptions || [])
        .filter((s) => s.status !== "opted_out")
        .map((s) => s.team)
        .filter(Boolean);

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent side="right" className="w-full sm:max-w-xl bg-paper border-border overflow-y-auto p-0" data-testid="customer-profile-sheet">
                <SheetHeader className="p-6 border-b border-border bg-gradient-to-br from-electric/10 to-transparent">
                    <SheetTitle className="text-left">
                        <div className="flex items-center gap-3">
                            <div className="h-12 w-12 rounded-full bg-electric/15 flex items-center justify-center">
                                <Phone className="h-5 w-5 text-electric" />
                            </div>
                            <div>
                                <div className="font-mono text-white text-lg">{phone}</div>
                                <div className="text-xs text-white/50 font-normal mt-0.5" data-testid="profile-header-subtitle">
                                    {data?.found ? (
                                        <>
                                            Customer profile ·{" "}
                                            {data.join_channel === "web" ? "joined via Web" :
                                             data.join_channel === "sms" ? "joined via SMS" :
                                             "joined channel unknown"}
                                        </>
                                    ) : (
                                        <>Customer profile</>
                                    )}
                                </div>
                            </div>
                        </div>
                    </SheetTitle>
                </SheetHeader>

                {loading && <div className="p-10 text-center text-white/50">Loading profile…</div>}

                {!loading && data && !data.found && (
                    <div className="p-10 text-center text-white/50" data-testid="profile-empty">
                        No activity recorded for this phone number yet.
                    </div>
                )}

                {!loading && data && data.found && (
                    <div className="p-6 space-y-5">
                        {/* Top stats */}
                        <div className="grid grid-cols-2 gap-2" data-testid="profile-stats">
                            <Stat label="Interactions" value={data.total_interactions} sub="All-time" />
                            <Stat label="Teams followed" value={teams.length} sub={teams.slice(0, 2).join(", ") || "—"} />
                            <Stat label="Last active" value={timeAgo(data.last_seen)} sub={fmt(data.last_seen)} />
                            <Stat label="First seen" value={timeAgo(data.first_seen)} sub={fmt(data.first_seen)} />
                        </div>

                        {/* Identity — join date/time, channels, last payment */}
                        <Card className="bg-obsidian border-border p-4" data-testid="profile-identity">
                            <div className="flex items-center gap-2 mb-3">
                                <LogIn className="h-4 w-4 text-electric" />
                                <span className="font-heading font-bold text-xs uppercase tracking-wider">Identity &amp; account</span>
                            </div>
                            <div className="space-y-0">
                                <IdentityRow
                                    icon={Calendar}
                                    label="Joined"
                                    value={
                                        <span className="tabular-nums" data-testid="identity-joined-at">
                                            {fmt(data.first_seen)}
                                            <span className="text-white/40 ml-2 text-xs">· {timeAgo(data.first_seen)}</span>
                                        </span>
                                    }
                                    extra={<ChannelBadge channel={data.join_channel} testId="identity-join-channel" />}
                                    testId="identity-joined-row"
                                />
                                <IdentityRow
                                    icon={Activity}
                                    label="Last active"
                                    value={
                                        <span className="tabular-nums" data-testid="identity-last-active-at">
                                            {fmt(data.last_seen)}
                                            <span className="text-white/40 ml-2 text-xs">· {timeAgo(data.last_seen)}</span>
                                        </span>
                                    }
                                    extra={<ChannelBadge channel={data.last_active_channel} testId="identity-last-channel" />}
                                    testId="identity-last-active-row"
                                />
                                <IdentityRow
                                    icon={CreditCard}
                                    label="Last payment / subscription"
                                    value={
                                        data.payments?.last_payment ? (
                                            <span className="tabular-nums" data-testid="identity-last-payment-summary">
                                                {data.payments.last_payment.amount != null
                                                    ? <><span className="text-electric font-bold">{data.payments.last_payment.currency || "TZS"} {Number(data.payments.last_payment.amount).toLocaleString()}</span><span className="text-white/60"> · {data.payments.last_payment.purpose || "payment"}</span></>
                                                    : <span className="text-white/80">Subscription confirmed</span>}
                                                <span className="text-white/40 ml-2 text-xs block sm:inline">
                                                    {fmt(data.payments.last_payment.at)} · {timeAgo(data.payments.last_payment.at)}
                                                </span>
                                            </span>
                                        ) : (
                                            <span className="text-white/40 italic">No successful payments yet</span>
                                        )
                                    }
                                    extra={data.payments?.last_payment ? <ChannelBadge channel={data.payments.last_payment.channel} testId="identity-payment-channel" /> : null}
                                    testId="identity-payment-row"
                                />
                                {data.payments && (data.payments.tx_count > 0 || data.prediction_slips?.paid > 0) && (
                                    <IdentityRow
                                        icon={Ticket}
                                        label="Lifetime totals"
                                        value={
                                            <span className="tabular-nums text-sm" data-testid="identity-totals">
                                                <span className="text-white">TZS {(data.payments.total_paid || 0).toLocaleString()}</span>
                                                <span className="text-white/40 mx-2">·</span>
                                                <span className="text-white/70">{data.payments.tx_count} payments</span>
                                                {data.prediction_slips?.paid > 0 && (
                                                    <>
                                                        <span className="text-white/40 mx-2">·</span>
                                                        <span className="text-white/70">{data.prediction_slips.paid} slips</span>
                                                    </>
                                                )}
                                            </span>
                                        }
                                        testId="identity-totals-row"
                                    />
                                )}
                            </div>
                        </Card>

                        {/* Teams */}
                        {data.subscriptions.length > 0 && (
                            <Card className="bg-obsidian border-border p-4" data-testid="profile-subscriptions">
                                <div className="flex items-center gap-2 mb-3">
                                    <Heart className="h-4 w-4 text-electric" />
                                    <span className="font-heading font-bold text-xs uppercase tracking-wider">Teams &amp; alerts</span>
                                </div>
                                <div className="space-y-2">
                                    {data.subscriptions.map((s) => {
                                        const meta = SUB_STATUS_META[s.status] || { icon: Activity, color: "text-white/60", label: s.status };
                                        const Icon = meta.icon;
                                        return (
                                            <div key={s.id} className="flex items-center justify-between bg-paper rounded-sm px-3 py-2" data-testid={`sub-${s.id}`}>
                                                <div className="flex items-center gap-2">
                                                    <Icon className={`h-3.5 w-3.5 ${meta.color}`} />
                                                    <span className="text-white font-medium text-sm">{s.team}</span>
                                                    <Badge variant="outline" className="border-border text-[10px] text-white/50 ml-1">
                                                        via {s.source}
                                                    </Badge>
                                                </div>
                                                <div className="text-[11px] text-white/40">
                                                    {s.last_sms_at ? `${s.total_sms_sent || 0} alerts · last ${timeAgo(s.last_sms_at)}` : `joined ${timeAgo(s.created_at)}`}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </Card>
                        )}

                        {/* Source mix */}
                        {data.sources_mix.length > 0 && (
                            <Card className="bg-obsidian border-border p-4" data-testid="profile-sources">
                                <div className="flex items-center gap-2 mb-3">
                                    <Activity className="h-4 w-4 text-electric" />
                                    <span className="font-heading font-bold text-xs uppercase tracking-wider">How they ask</span>
                                </div>
                                <div className="space-y-2">
                                    {data.sources_mix.map((s) => {
                                        const pct = data.total_interactions ? Math.round((s.count / data.total_interactions) * 100) : 0;
                                        return (
                                            <div key={s.source} className="space-y-1">
                                                <div className="flex items-center justify-between text-xs">
                                                    <SourcePill source={s.source} />
                                                    <span className="text-white/60 font-mono">{s.count} ({pct}%)</span>
                                                </div>
                                                <div className="h-1.5 bg-paper rounded-full overflow-hidden">
                                                    <div className="h-full bg-electric" style={{ width: `${pct}%` }} />
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </Card>
                        )}

                        {/* Recent interactions */}
                        <Card className="bg-obsidian border-border p-4" data-testid="profile-interactions">
                            <div className="flex items-center gap-2 mb-3">
                                <Inbox className="h-4 w-4 text-electric" />
                                <span className="font-heading font-bold text-xs uppercase tracking-wider">
                                    Recent SMS · last {Math.min(data.interactions.length, 10)}
                                </span>
                            </div>
                            <div className="space-y-2 max-h-72 overflow-y-auto">
                                {data.interactions.length === 0 && (
                                    <div className="text-xs text-white/40">No SMS questions yet.</div>
                                )}
                                {data.interactions.slice(0, 10).map((i) => (
                                    <div key={i.id} className="bg-paper rounded-sm p-3" data-testid={`interaction-${i.id}`}>
                                        <div className="flex items-center justify-between mb-1.5">
                                            <SourcePill source={i.source} />
                                            <span className="text-[10px] text-white/40 flex items-center gap-1">
                                                <Clock className="h-2.5 w-2.5" /> {fmt(i.created_at)}
                                            </span>
                                        </div>
                                        <div className="text-xs text-white/90 mb-0.5">
                                            <span className="text-white/40 mr-1">Q:</span>{i.message || "—"}
                                        </div>
                                        <div className="text-xs text-white/60">
                                            <span className="text-electric mr-1 font-bold">A:</span>{i.response}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </Card>

                        {/* Content the user has been engaged with */}
                        {data.consumed_content.length > 0 && (
                            <Card className="bg-obsidian border-border p-4" data-testid="profile-consumed">
                                <div className="flex items-center gap-2 mb-3">
                                    <BookOpen className="h-4 w-4 text-electric" />
                                    <span className="font-heading font-bold text-xs uppercase tracking-wider">Likely interested in</span>
                                </div>
                                <div className="space-y-2 max-h-60 overflow-y-auto">
                                    {data.consumed_content.map((c) => (
                                        <a
                                            key={c.id}
                                            href={`/makala/${c.id}`}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="flex items-start gap-3 bg-paper rounded-sm p-2 hover:bg-subtle/40 transition-colors group"
                                            data-testid={`consumed-${c.id}`}
                                        >
                                            {c.image_url && (
                                                <img
                                                    src={c.image_url}
                                                    alt=""
                                                    className="h-10 w-14 object-cover rounded-sm flex-shrink-0"
                                                    onError={(e) => { e.target.style.display = "none"; }}
                                                />
                                            )}
                                            <div className="flex-1 min-w-0">
                                                <div className="text-xs text-white/90 line-clamp-2 group-hover:text-electric transition-colors">
                                                    {c.title_sw || c.title}
                                                </div>
                                                <div className="text-[10px] text-white/40 mt-0.5 flex items-center gap-2">
                                                    {c.sport && <span>{c.sport}</span>}
                                                    {c.team && <span>· {c.team}</span>}
                                                    {c.published_at && <span>· {timeAgo(c.published_at)}</span>}
                                                </div>
                                            </div>
                                        </a>
                                    ))}
                                </div>
                            </Card>
                        )}

                        {/* Marketing audiences */}
                        {data.customer_bases.length > 0 && (
                            <Card className="bg-obsidian border-border p-4" data-testid="profile-bases">
                                <div className="flex items-center gap-2 mb-3">
                                    <Users className="h-4 w-4 text-electric" />
                                    <span className="font-heading font-bold text-xs uppercase tracking-wider">In marketing lists</span>
                                </div>
                                <div className="flex flex-wrap gap-1.5">
                                    {data.customer_bases.map((b) => (
                                        <Badge key={b.id} variant="outline" className="border-electric/40 text-electric">
                                            {b.name}
                                        </Badge>
                                    ))}
                                </div>
                            </Card>
                        )}

                        {/* Outbound SMS sent to this phone */}
                        {data.outbound.length > 0 && (
                            <Card className="bg-obsidian border-border p-4" data-testid="profile-outbound">
                                <div className="flex items-center gap-2 mb-3">
                                    <Send className="h-4 w-4 text-electric" />
                                    <span className="font-heading font-bold text-xs uppercase tracking-wider">
                                        Outbound · {data.outbound.length}
                                    </span>
                                </div>
                                <div className="space-y-1.5 max-h-48 overflow-y-auto">
                                    {data.outbound.map((o) => (
                                        <div key={o.id} className="text-xs bg-paper rounded-sm p-2">
                                            <div className="flex justify-between text-[10px] text-white/40 mb-0.5">
                                                <span>{o.status}</span>
                                                <span>{fmt(o.created_at)}</span>
                                            </div>
                                            <div className="text-white/80">{o.message}</div>
                                        </div>
                                    ))}
                                </div>
                            </Card>
                        )}

                        <Card className="bg-obsidian border-border p-4" data-testid="profile-for-you">
                            <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-2">
                                    <Mail className="h-4 w-4 text-electric" />
                                    <span className="font-heading font-bold text-xs uppercase tracking-wider">For-You digest</span>
                                </div>
                                <div className="flex gap-1.5">
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={loadPulse}
                                        disabled={pulseLoading}
                                        className="h-7 text-xs"
                                        data-testid="for-you-preview-btn"
                                    >
                                        {pulseLoading ? "…" : "Preview"}
                                    </Button>
                                    <Button
                                        size="sm"
                                        onClick={sendPulse}
                                        disabled={pulseSending}
                                        className="h-7 text-xs bg-electric hover:bg-electric/90"
                                        data-testid="for-you-send-btn"
                                    >
                                        <Send className="h-3 w-3 mr-1" /> {pulseSending ? "Sending…" : "Send now"}
                                    </Button>
                                </div>
                            </div>
                            {!pulse && (
                                <p className="text-xs text-white/40">
                                    Click <span className="text-electric">Preview</span> to compose a personalised Swahili digest
                                    based on this user's questions + team subscriptions. Capped at 160 chars.
                                </p>
                            )}
                            {pulse && !pulse.has_content && (
                                <p className="text-xs text-white/60" data-testid="for-you-empty">
                                    No relevant article or upcoming prediction matches this user's interests yet.
                                </p>
                            )}
                            {pulse && pulse.has_content && (
                                <div className="space-y-2" data-testid="for-you-preview">
                                    <div className="bg-paper rounded-sm p-3">
                                        <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1">SMS body</div>
                                        <div className="text-sm text-white/90 leading-relaxed">{pulse.message}</div>
                                        <div className="text-[10px] text-white/40 mt-2 font-mono">{pulse.character_count} / 160 chars</div>
                                    </div>
                                    {(pulse.priority_terms?.length > 0 || pulse.weak_terms?.length > 0) && (
                                        <div className="flex flex-wrap gap-1">
                                            {(pulse.priority_terms || []).map((t) => (
                                                <Badge key={`p-${t}`} className="bg-electric/20 text-electric border-0 text-[10px]">{t}</Badge>
                                            ))}
                                            {(pulse.weak_terms || []).map((t) => (
                                                <Badge key={`w-${t}`} variant="outline" className="border-border text-white/50 text-[10px]">{t}</Badge>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </Card>

                        <div className="pt-2 flex justify-end">
                            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="profile-close-btn">
                                Close
                            </Button>
                        </div>
                    </div>
                )}
            </SheetContent>
        </Sheet>
    );
};

export default CustomerProfileSheet;
